import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Camera, CameraOff, ScanLine, Loader2, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import api, { API, formatApiError } from "@/lib/api";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

let _ac;
function feedback(ok = true) {
  try {
    _ac = _ac || new (window.AudioContext || window.webkitAudioContext)();
    if (_ac.state === "suspended") _ac.resume();
    const o = _ac.createOscillator();
    const g = _ac.createGain();
    o.connect(g); g.connect(_ac.destination);
    o.type = "sine";
    o.frequency.value = ok ? 880 : 240;
    g.gain.setValueAtTime(0.18, _ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, _ac.currentTime + 0.18);
    o.start(); o.stop(_ac.currentTime + 0.19);
  } catch { /* ignore */ }
  try { navigator.vibrate?.(ok ? 60 : [40, 50, 40]); } catch { /* ignore */ }
}

const COND = {
  NM: "bg-emerald-950/80 text-emerald-400 border-emerald-800/60",
  LP: "bg-lime-950/80 text-lime-400 border-lime-800/60",
  MP: "bg-amber-950/80 text-amber-400 border-amber-800/60",
  HP: "bg-orange-950/80 text-orange-400 border-orange-800/60",
  DMG: "bg-rose-950/80 text-rose-400 border-rose-800/60",
};

export default function ScanRepricer() {
  const [scanning, setScanning] = useState(false);
  const [last, setLast] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const scannerRef = useRef(null);
  const lastRef = useRef({ text: "", ts: 0 });
  const inputRef = useRef(null);

  const lookup = useCallback(async (token) => {
    const t = (token || "").trim();
    if (!t) return;
    const now = Date.now();
    if (t === lastRef.current.text && now - lastRef.current.ts < 1200) return;
    lastRef.current = { text: t, ts: now };
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/cards/by-qr/${encodeURIComponent(t)}`);
      setLast(data);
      feedback(true);
      setLoading(false);
      // Auto-pull the most recent live price for this card right after scanning.
      if (data?.id && !data.price_override) {
        setRefreshing(true);
        try {
          const { data: r } = await api.post(`/cards/${data.id}/reprice-now`);
          setLast(r.card);
          if (r.updated) toast.success(`Updated to live price: ${fmt(r.card.tcg_price)}`);
        } catch { /* keep stored price if live fetch fails */ }
        finally { setRefreshing(false); }
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "No card found");
      feedback(false);
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!last?.id) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/cards/${last.id}/reprice-now`);
      setLast(data.card);
      feedback(true);
      toast.success(data.updated ? `Live price: ${fmt(data.card.tcg_price)}` : "No fresh data — price unchanged");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); feedback(false); }
    finally { setRefreshing(false); }
  }, [last]);

  useEffect(() => {
    if (!scanning) return;
    const scanner = new Html5Qrcode("reprice-scan-region", { verbose: false });
    scannerRef.current = scanner;
    scanner.start({ facingMode: "environment" }, { fps: 12, qrbox: { width: 200, height: 200 } },
      (text) => lookup(text), () => {}).catch(() => setError("Could not start camera"));
    return () => { const s = scannerRef.current; if (s) s.stop().then(() => s.clear()).catch(() => {}); };
  }, [scanning, lookup]);

  const onInputKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); lookup(inputRef.current.value); inputRef.current.value = ""; }
  };

  return (
    <div className="rounded-2xl border p-5 mb-6" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="scan-repricer">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-100 flex items-center gap-2"><ScanLine className="h-5 w-5 text-amber-400" /> Scan to check price</h2>
          <p className="text-xs text-slate-500 mt-0.5">Scan a card's QR sticker and it automatically pulls the most recent price for that condition — write it on the front sticker and scan the next.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Scanner */}
        <div>
          <div className="flex gap-2 mb-3">
            {!scanning ? (
              <button onClick={() => { setScanning(true); setError(""); }} data-testid="reprice-scan-start"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400">
                <Camera className="h-4 w-4" /> Start scanning
              </button>
            ) : (
              <button onClick={() => setScanning(false)} data-testid="reprice-scan-stop"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-rose-800/60 text-rose-400 font-semibold text-sm hover:bg-rose-500/10">
                <CameraOff className="h-4 w-4" /> Stop camera
              </button>
            )}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl border flex-1" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
              <ScanLine className="h-4 w-4 text-slate-500" />
              <input ref={inputRef} onKeyDown={onInputKey} data-testid="reprice-scan-input"
                placeholder="or scan/type code + Enter" className="w-full bg-transparent outline-none text-xs text-slate-100 placeholder:text-slate-600 font-mono-num" />
            </div>
          </div>
          <div id="reprice-scan-region" className={`w-full rounded-xl overflow-hidden ${scanning ? "border" : ""}`} style={{ borderColor: "var(--pk-border)", minHeight: scanning ? 220 : 0 }} />
          {!scanning && <div className="rounded-xl border border-dashed flex items-center justify-center h-40 text-slate-600 text-sm" style={{ borderColor: "var(--pk-border)" }}>Camera off — press Start scanning</div>}
        </div>

        {/* Last scan */}
        <div className="rounded-xl border p-5 flex flex-col justify-center" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="reprice-last-scan">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Last scan</div>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Looking up…</div>
          ) : error ? (
            <div className="flex items-center gap-2 text-rose-400 text-sm" data-testid="reprice-scan-error"><XCircle className="h-5 w-5" /> {error}</div>
          ) : last ? (
            <div className="flex items-center gap-4">
              <div className="h-24 w-16 rounded-lg bg-[var(--pk-surface)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
                {last.image_url ? <img src={last.image_url.startsWith("/api") ? `${API.replace("/api", "")}${last.image_url}` : last.image_url} alt="" className="h-full w-full object-cover" /> : <CheckCircle2 className="h-5 w-5 text-slate-700" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-base font-bold text-slate-100 truncate" data-testid="reprice-scan-name">{last.name}</div>
                <div className="text-xs text-slate-500 truncate">{last.set_name}{last.card_number ? ` · #${last.card_number}` : ""}</div>
                <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${COND[last.condition] || COND.NM}`}>{last.condition}</span>
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    Current price
                    {refreshing && <span className="inline-flex items-center gap-1 text-amber-400/80 normal-case tracking-normal font-normal"><Loader2 className="h-3 w-3 animate-spin" /> fetching latest…</span>}
                  </div>
                  <div className="font-mono-num text-4xl font-extrabold text-amber-400 leading-none mt-1" data-testid="reprice-scan-price">{fmt(last.tcg_price)}</div>
                  {last.price_override ? <div className="text-[11px] text-slate-500 mt-1">manual override — not auto-priced</div> : last.tcg_market_price ? <div className="text-[11px] text-slate-500 mt-1">NM market {fmt(last.tcg_market_price)}</div> : null}
                </div>
                <button onClick={refresh} disabled={refreshing} data-testid="reprice-refresh-price"
                  className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs text-slate-200 hover:bg-[var(--pk-hover)] disabled:opacity-60" style={{ borderColor: "var(--pk-border)" }}>
                  {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh again
                </button>
              </div>
            </div>
          ) : (
            <div className="text-slate-600 text-sm">Scan a card to see its current price here.</div>
          )}
        </div>
      </div>
    </div>
  );
}
